import {Spin} from "antd";
import {
    DeleteOutlined,
    EyeOutlined,
    FileOutlined,
    PictureOutlined,
    SoundOutlined,
    VideoCameraOutlined,
} from "@ant-design/icons";
import {UploadFile} from "antd/es/upload/interface";
import {
    AnyElementValue,
    ComparisonItems,
    ComparisonReferences,
    EditorType,
    ElementsValues,
    getComparisonItems,
    Nullable,
    UploadsConfig,
    validateComparisonConstraints,
} from "@data-force/app";
import FileType, {FileTypeResult} from "file-type/browser";
import {AnyElementType} from "@data-force/app/interfaces/store/actions/payload/schema/schemaElements";
import React from "react";
import {ArgsProps} from "antd/es/message";
import heic2any from "heic2any";
import {
    AvailableHolderData,
    CreateTaskFileData,
    DisplayErrorsData,
    ErrorMessageData,
    ErrorsData,
    FileIsValidHolder,
    FileReferenceValidationData,
    FileTypeAndExtensionData,
    FileValidationInfo,
    FileValidity,
    GetReferenceValueData,
    HandleMediaInfoResult,
    IconData,
    MapFileToTaskData,
    MimeTypeValidationData,
    ReferenceValidationData,
    ReferenceValidationResult,
    ReferenceValueValidationResult,
    SniffFileTypeResult,
    Thumbnails,
    UploadsAndReferenceValidationData,
    ValidationData,
    ValidByReferenceResult,
} from "../../interfaces/components/FilePickerTypes";
import {TaskFile} from "../../interfaces/store/actions/payload/uploadFiles";
import {Task, TaskEntity} from "../../interfaces/store/actions/payload/task";
import {FileTypeAndExtension, UploadsHolder} from "../../interfaces/store/actions/payload/fileValidation";
import {Result, ResultObject, Track} from "../../types/types";
import {getFileMediaInfo} from "./MediaInfoUtils";
import {
    EXIF_ASPECT_RATIO,
    EXIF_MAKE,
    EXIF_MODEL,
    EXIF_RESOLUTION,
    HTML_SUPPORTED_AUDIO,
    HTML_SUPPORTED_IMAGE,
    HTML_SUPPORTED_VIDEO,
    MAKE_MODEL_INVALID_TRANSLATION_PATH,
    REFERENCE_UNIT_MAP,
    UPLOADS_REFERENCE_TRANSLATION_PATH,
} from "../../utils/constants";
import {
    determineBucketKey, flattenObject, formatString, getComparatorValues,
} from "../../utils/common";
import VideoThumbnail from "./VideoThumbnail/VideoThumbnail";
import {UploadsBucket} from "../../interfaces/store/reducers/uploadFilesState";
import {FileSelectionType} from "../../interfaces/store/actions/payload/layout";

/**
 * Determines file extension.
 *
 * @param {string} fileName
 * @return {string}
 */
export const determineFileExtension = (fileName: string): Nullable<string> => {
    let extension: Nullable<string> = null;
    const extensionSeparator: number = fileName.lastIndexOf(".");
    if (extensionSeparator > -1) {
        extension = fileName.substring(extensionSeparator + 1);
    }
    return extension;
};

/**
 * Uses 3td party file-type library to determine type of provided file.
 *
 * @param {Nullable<File | Blob>} fileObj
 * @return {Promise<Nullable<FileTypeResult>>}
 */

export const sniffFileType = async (fileObj: Nullable<File | Blob>): Promise<Nullable<FileTypeResult>> => {
    let sniffedFileType: Nullable<FileTypeResult> = null;
    if (fileObj) {
        sniffedFileType = await FileType.fromBlob(fileObj as Blob);
    }
    return sniffedFileType;
};

/**
 * Checks if file is valid by schema configured mimeTypes.
 *
 * @param {MimeTypeValidationData} validationData
 * @return {boolean}
 */
export const fileIsValidByMimeType = (validationData: MimeTypeValidationData): boolean => {
    const {
        acceptedMimeTypes, fileMimeType, fileName, fileExtension,
    }: MimeTypeValidationData = validationData;
    const [fileType, fileSubType] = fileMimeType.split("/");

    let fileValid: boolean = false;
    for (let i = 0; i < acceptedMimeTypes.length; i += 1) {
        const [type, subtype] = acceptedMimeTypes[i].split("/");
        if (type && subtype
            && (type === "*" || (type === fileType && (subtype === "*" || subtype === fileSubType)))) {
            fileValid = true;
        }
    }
    if (!fileValid) {
        const extension: Nullable<string> = fileExtension || determineFileExtension(fileName);
        if (extension) {
            fileValid = acceptedMimeTypes.includes(`.${extension}`);
        }
    }

    return fileValid;
};

/**
 * Maps provided file to available task
 *
 * @param {CreateTaskFileData} data
 * @return {Nullable<TaskFile>}
 */
export const createTaskFile = (data: CreateTaskFileData): Nullable<TaskFile> => {
    const {
        tasks, mappedTaskIds, uploadFile, finishedTaskIds,
    }: CreateTaskFileData = data;
    let taskFile: Nullable<TaskFile> = null;
    const taskEntity: Nullable<TaskEntity> = tasks.find(
        (task: TaskEntity): boolean => !mappedTaskIds.includes(task.payload) && !finishedTaskIds.includes(task.id),
    );
    if (taskEntity) {
        const {payload: taskId}: TaskEntity = taskEntity;
        taskFile = {taskId, uploadFile};
    }

    return taskFile;
};

/**
 * Maps provided file to activeTask, or any available task.
 *
 * @param {MapFileToTaskData} data
 * @return {TaskFile}
 */
export const mapFileToTask = (data: MapFileToTaskData): Nullable<TaskFile> => {
    const {
        fileSelectionType,
        activeTask,
        tasks,
        mappedTaskIds,
        uploadFile,
        finishedTaskIds,
    }: MapFileToTaskData = data;
    let taskFile: Nullable<TaskFile>;

    if (activeTask && fileSelectionType === FileSelectionType.SINGLE) {
        const {id: taskId}: Task = activeTask;
        taskFile = {taskId, uploadFile};
    } else {
        taskFile = createTaskFile({
            uploadFile, mappedTaskIds, tasks, finishedTaskIds,
        });
    }

    return taskFile;
};

/**
 * Eliminates duplicated files, by file name.
 *
 * @param {UploadFile[]} fileList
 * @return {UploadFile[]}
 */
export const eliminateDuplicateFiles = (fileList: UploadFile[]): UploadFile[] => fileList.filter(
    (file: UploadFile, index: number, self: UploadFile[]): boolean => index === self.findIndex(
        (f: UploadFile): boolean => (f.name === file.name),
    ),
);

/**
 * Determines file type and extension simultaneously.
 *
 * @param {FileTypeAndExtensionData} fileData
 * @return {FileTypeAndExtension}
 */
export const determineFileTypeAndExtension = (fileData: FileTypeAndExtensionData): FileTypeAndExtension => {
    const {sniffedType, type}: FileTypeAndExtensionData = fileData;
    let fileType: string;
    let fileExtension: Nullable<string>;
    if (sniffedType) {
        const {ext, mime}: FileTypeResult = sniffedType;
        fileType = mime ? mime as string : type;
        fileExtension = ext ? ext as string : null;
    } else {
        fileType = type;
        fileExtension = null;
    }

    return {fileType, fileExtension};
};

/**
 * Performs "MediaInfo" (exif) analysis, of the currently uploaded file.
 * Then initiate file validation and update of "upload files" list.
 *
 * @param {UploadFile} file
 * @param {Nullable<FileTypeResult>} sniffedType
 * @return {Promise<HandleMediaInfoResult>}
 */
export const handleMediaInfo = (file: UploadFile, sniffedType: Nullable<FileTypeResult>)
: Promise<HandleMediaInfoResult> => {
    const {type}: UploadFile = file;
    const fileTypeAndExtension: FileTypeAndExtension = determineFileTypeAndExtension({
        sniffedType, type: type || "",
    });
    const mediaInfo: Promise<Nullable<Result>> = getFileMediaInfo(file, fileTypeAndExtension);

    return Promise.all([file, sniffedType, mediaInfo, fileTypeAndExtension]);
};

/**
 * Determines file type.
 * Then triggers "MediaInfo" (exif) analysis, of the currently uploaded file.
 *
 * @param {UploadFile} file
 */
export const handleTypeAndMediaInfo = (file: UploadFile): Promise<HandleMediaInfoResult> => {
    const sniffedType: Promise<Nullable<FileTypeResult>> = sniffFileType(file.originFileObj);
    return Promise.all([file, sniffedType])
        .then((values: SniffFileTypeResult) => handleMediaInfo(...values));
};

/**
 * Preventing automatic upload
 */
export const beforeUploadHandler = (): boolean => false;

/**
 * Maps comparison errors to uploads - validation reference
 *
 * @param {ErrorsData} errorsData
 * @param {boolean} addReferenceToMessage
 * @return {string[]}
 */
export const createValidationErrors = (errorsData: ErrorsData, addReferenceToMessage: boolean): string[] => {
    const {errors, reference}: ErrorsData = errorsData;
    const uniqErrors: string[] = [...new Set(errors)];
    const messageArray: string[] = [];
    uniqErrors.forEach((error: string): void => {
        const unit: string = REFERENCE_UNIT_MAP[reference] || "";
        const errorWithUnits = formatString(error, "", unit);
        const isMakeModelError: boolean = [EXIF_MODEL, EXIF_MAKE].includes(reference);
        const message: string = addReferenceToMessage ? `${reference} ${errorWithUnits}` : `${errorWithUnits}`;
        messageArray.push(isMakeModelError ? error : message);
    });
    return messageArray;
};

/**
 * Calculates, schema configured, acceptable mimeType-s
 *
 * @param {Nullable<EditorType>} editor
 * @return {string[]}
 */
export const getAcceptedMimeTypes = (editor: Nullable<EditorType>): string[] => {
    let mimeTypes: string[] = ["*/*"];
    if (editor) {
        const {batchUploads, acceptedMimeTypes}: EditorType = editor;
        if (batchUploads) {
            batchUploads.forEach((uploadConfig: UploadsConfig) => {
                mimeTypes = mimeTypes.concat(uploadConfig.mimeTypes);
            });
        } else if (acceptedMimeTypes) {
            mimeTypes = acceptedMimeTypes;
        }
    }
    return [...new Set(mimeTypes)];
};

/**
 * This method is a temporary solution for calculating resolution and aspect ratio of media item,
 * proper dynamic validation should be implemented in next iteration
 * @param {Track} track
 * @param {String} reference
 * @return {string[]}
 */
export const getCalculatedReferenceValue = (track: Track, reference: string): string[] => {
    const { Height, Width} = track;
    let value: number | boolean = 0;
    if (reference === EXIF_RESOLUTION) {
        value = (Number(Height) * Number(Width)) / 1000000;
    }
    if (reference === EXIF_ASPECT_RATIO) {
        value = Math.round((Number(Width) / Number(Height)) * 100) / 100;
    }
    return [value.toString()];
};

/**
 * Returns file exif values, for provided validation reference.
 *
 * @param {GetReferenceValueData} valueData
 * @return {Nullable<AnyElementValue>[]}
 */
export const getUploadsReferenceValues = (valueData: GetReferenceValueData): Nullable<AnyElementValue>[] => {
    const {reference, fileMediaInfo}: GetReferenceValueData = valueData;
    const value: Nullable<AnyElementValue>[] = [];
    if (fileMediaInfo) {
        const resultObject: ResultObject = fileMediaInfo as ResultObject;
        if (resultObject.media?.track) {
            const tracks: Track[] = resultObject.media.track.map(
                (track: Track): Track => flattenObject(track),
            );

            tracks.forEach((track: Track): void => {
                if (track[reference]) {
                    value.push(track[reference]);
                } else if ((track["@type"] === "Image" || track["@type"] === "Video")
                    && (reference === EXIF_RESOLUTION || reference === EXIF_ASPECT_RATIO)) {
                    // case when reference isn't directly found on track object,
                    // then we calculate value based on available properties
                    value.push(getCalculatedReferenceValue(track, reference));
                }
            });
        }
    }
    return value.filter((val: Nullable<AnyElementValue>): boolean => !!val);
};

/**
 * Determines if file is valid, for provided exif values of validation reference.
 * If not, includes corresponding validation errors in to response.
 *
 * @param {ReferenceValidationData} validationData
 * @return {ReferenceValueValidationResult}
 */
export const fileIsValidByReferenceValues = (
    validationData: ReferenceValidationData,
): ReferenceValueValidationResult => {
    const {
        elementsValues,
        translate,
        reference,
        referenceValues,
        element,
    }: ReferenceValidationData = validationData;

    const data: ElementsValues = {...elementsValues};
    const errors: string[] = [];

    const referenceValidity: boolean[] = referenceValues.map((value: Nullable<AnyElementValue>) => {
        let isValidReference: boolean = true;
        data[reference] = value!;

        // TODO this needs to be implemented dynamically in next iteration
        // temporary fix so we can correctly calculate if file aspect ratio is equal to schemas ratio
        let storedRatio: string = "";
        let schemaRatio: string = "";
        if (element?.fieldName === EXIF_ASPECT_RATIO) {
            //  @ts-ignore
            schemaRatio = data[element.eqReferences[0]];
            const ratios: string[] = schemaRatio.split(":");
            storedRatio = (Math.round((Number(ratios[0]) / Number(ratios[1])) * 100) / 100).toString();
            // @ts-ignore
            data[element.eqReferences[0]] = storedRatio;
        }
        const comparisonItems: Nullable<ComparisonItems> = getComparisonItems(element!, data);
        if (comparisonItems) {
            let comparisonError: Nullable<string> = validateComparisonConstraints({
                value: value!,
                comparisonItems,
                fieldTranslatePath: UPLOADS_REFERENCE_TRANSLATION_PATH,
                translate,
            });
            isValidReference = !comparisonError;

            if (comparisonError) {
                switch (element?.fieldName) {
                    case EXIF_ASPECT_RATIO:
                        comparisonError = comparisonError.replace(storedRatio, schemaRatio);
                        break;
                    case EXIF_MAKE:
                        comparisonError = formatString(
                            translate(MAKE_MODEL_INVALID_TRANSLATION_PATH) as string,
                            getComparatorValues(comparisonItems, ComparisonReferences.EQ)[0] as string,
                        );
                        break;
                    case EXIF_MODEL:
                        comparisonError = formatString(
                            translate(MAKE_MODEL_INVALID_TRANSLATION_PATH) as string,
                            getComparatorValues(comparisonItems, ComparisonReferences.EQ)[0] as string,
                        );
                        break;
                    default:
                        break;
                }
                errors.push(comparisonError);
            }
        }
        if (element?.fieldName === EXIF_ASPECT_RATIO) {
            // @ts-ignore
            data[element.eqReferences[0]] = schemaRatio;
        }

        return isValidReference;
    });

    return {referenceValidity, errors};
};

/**
 * Determines if file is valid by provided (uploads) validation references.
 * If not, creates corresponding error messages.
 *
 * @param {FileReferenceValidationData} data
 * @return {ReferenceValidationResult}
 */
export const getFileReferenceValidity = (data: FileReferenceValidationData): ReferenceValidationResult => {
    const {
        collectionRefElements,
        translate,
        elementsValues,
        fileMediaInfo,
        validationReferences,
        translatePath,
    }: FileReferenceValidationData = data;
    let fileErrors: string[] = [];
    const fileValidity: boolean[] = validationReferences.map((reference: string): boolean => {
        let isFileValid: boolean = false;
        const element: Nullable<AnyElementType> = collectionRefElements[reference];
        if (element) {
            const referenceValues: Nullable<AnyElementValue>[] = getUploadsReferenceValues({
                reference,
                fileMediaInfo,
            });
            if (referenceValues.length) {
                const referenceValidation: ReferenceValueValidationResult = fileIsValidByReferenceValues({
                    reference,
                    referenceValues,
                    element,
                    translate,
                    elementsValues,
                });
                const {referenceValidity, errors}: ReferenceValueValidationResult = referenceValidation;
                const invalidReferenceIndex: number = referenceValidity.findIndex(
                    (isValid: boolean): boolean => !isValid,
                );

                isFileValid = (invalidReferenceIndex === -1);
                if (!isFileValid) {
                    fileErrors = fileErrors.concat(createValidationErrors(({errors, reference}), true));
                }
            } else {
                const dataUnavailable: string = translate(`${translatePath}.fileDataUnavailable.text`) as string;
                const dataUnavailableMessage: string = formatString(dataUnavailable, reference.toLowerCase());
                fileErrors = fileErrors.concat(createValidationErrors(({
                    errors: [dataUnavailableMessage],
                    reference,
                }), false));
            }
        }

        return isFileValid;
    });

    return {fileValidity, errors: fileErrors};
};

/**
 * Checks if file is valid by uploads validation references.
 * Display error messages if it isn't.
 *
 * @param {UploadsAndReferenceValidationData} validationData
 * @return {ValidByReferenceResult}
 */
export const fileIsValidByReferences = (validationData: UploadsAndReferenceValidationData): ValidByReferenceResult => {
    const {
        fileMediaInfo,
        validationReferences,
        elementsValues,
        collectionRefElements,
        translate,
        translatePath,
    }: UploadsAndReferenceValidationData = validationData;

    const refValidity: ReferenceValidationResult = getFileReferenceValidity({
        validationReferences,
        collectionRefElements,
        fileMediaInfo,
        elementsValues,
        translate,
        translatePath,
    });
    const {errors, fileValidity}: ReferenceValidationResult = refValidity;
    const invalidReferenceIndex = fileValidity.findIndex((isValid: boolean): boolean => !isValid);

    return {
        isValidByReference: invalidReferenceIndex === -1,
        errors,
    };
};

/**
 * Display validation errors for single file.
 *
 * @param {DisplayErrorsData} errorsData
 * @return {Nullable<ArgsProps>}
 */
export const createReferenceAlert = (errorsData: DisplayErrorsData): Nullable<ArgsProps> => {
    const {errors, fileName, holderIndex}: DisplayErrorsData = errorsData;
    let alert: Nullable<ArgsProps> = null;
    if (errors?.length) {
        const errorMessage: string = `${fileName} - ${errors.join(", ")}`;
        alert = {
            content: errorMessage,
            duration: 0,
            type: "error",
            key: `${fileName}-${holderIndex}-errors`,
        };
    }
    return alert;
};

/**
 * Create error alerts, when validating file.
 *
 * @param {FileValidationInfo} validationInfo
 * @return {ArgsProps[]}
 */
export const createValidationAlerts = (validationInfo: FileValidationInfo): ArgsProps[] => {
    const {
        fileName, validByMimeType, validByReferences, fileIsValidHolders, translate,
    }: FileValidationInfo = validationInfo;
    const validationAlerts: ArgsProps[] = [];
    if (!validByMimeType) {
        const mimeTypeError: string = translate(
            "components.SingleFilePicker.SingleFilePicker.wrongFileFormat.text",
        ) as string;
        validationAlerts.push({
            content: `${fileName} - ${mimeTypeError}`,
            duration: 0,
            type: "error",
            key: `${fileName}-mime-errors`,
        });
    } else if (!validByReferences) {
        fileIsValidHolders.forEach(({byMimeType, errors}: FileIsValidHolder, index: number): void => {
            if (byMimeType && errors.length) {
                const referenceAlert: Nullable<ArgsProps> = createReferenceAlert({
                    fileName, errors, holderIndex: index,
                });
                if (referenceAlert) {
                    validationAlerts.push(referenceAlert);
                }
            }
        });
    }
    return validationAlerts;
};

/**
 * Checks if file is valid by mimeTypes, and validation references.
 *
 * @param {FileValidationData} fileValidationData
 * @return {boolean}
 */
export const fileIsValid = (fileValidationData: ValidationData): FileValidity => {
    const {
        uploadsData: {uploadsHolders, acceptedMimeTypes},
        elementsValues,
        collectionRefElements,
        translate,
        fileName,
        fileExtension,
        fileMimeType,
        fileMediaInfo,
        translatePath,
        upload,
    }: ValidationData = fileValidationData;

    let validHolders: UploadsHolder[] = uploadsHolders;
    if (upload) {
        const reopenedHolder: Nullable<UploadsHolder> = uploadsHolders.find(
            (holder: UploadsHolder) => holder.uploadsConfig.upload === upload,
        );
        if (reopenedHolder) {
            validHolders = [reopenedHolder];
        }
    }

    const fileValidity: FileIsValidHolder[] = validHolders.map((uploadsHolder: UploadsHolder): FileIsValidHolder => {
        const {uploadsConfig}: UploadsHolder = uploadsHolder;
        const {mimeTypes, validationReferences = []}: UploadsConfig = uploadsConfig;
        const byMimeType: boolean = fileIsValidByMimeType({
            acceptedMimeTypes: mimeTypes,
            fileExtension,
            fileMimeType,
            fileName,
        });
        const {isValidByReference, errors}: ValidByReferenceResult = byMimeType
            ? fileIsValidByReferences({
                validationReferences,
                fileMediaInfo,
                fileExtension,
                elementsValues,
                collectionRefElements,
                translate,
                translatePath,
            })
            : {isValidByReference: false, errors: []};
        const byReferences: boolean = byMimeType && isValidByReference;

        return {
            byMimeType, byReferences, uploadsHolder, errors,
        };
    });

    const validByMimeType: boolean = fileValidity.findIndex(
        (valid: FileIsValidHolder): boolean => valid.byMimeType,
    ) > -1
        || fileIsValidByMimeType({ // backward compatibility
            acceptedMimeTypes,
            fileExtension,
            fileMimeType,
            fileName,
        });

    const validByReferences: boolean = validByMimeType && (!uploadsHolders.length
        || fileValidity.some((valid: FileIsValidHolder) => valid.byMimeType && valid.byReferences));

    const validationAlerts: ArgsProps[] = createValidationAlerts({
        fileName,
        validByMimeType,
        validByReferences,
        fileIsValidHolders: fileValidity,
        translate,
    });
    const validUploadsHolders: UploadsHolder[] = validByReferences
        ? fileValidity.filter((valid: FileIsValidHolder): boolean => valid.byMimeType && valid.byReferences)
            .map((fileIsValidHolder: FileIsValidHolder): UploadsHolder => fileIsValidHolder.uploadsHolder)
        : [];

    return {
        isFileValid: validByMimeType && validByReferences,
        validUploadsHolders,
        validationAlerts,
    };
};
/**
 * Generates a blobUrl for HTML supported files preview.
 *
 * @param {File | Blob} data
 * @return {Promise<string>}
 */
export const generatePreviewFile = (data: File | Blob): Promise<string> => {
    const {type} : File | Blob = data;

    if (type === "image/heic") {
        return heic2any({
            blob: data,
        }).then(
            // casting to Blob as we are always using just a single image so it will never be Blob[]
            (result) => window.URL.createObjectURL(result as Blob),
        );
    }

    const isViewable: boolean = [
        ...HTML_SUPPORTED_VIDEO,
        ...HTML_SUPPORTED_AUDIO,
        ...HTML_SUPPORTED_IMAGE,
    ].includes(type);
    return Promise.resolve<string>(isViewable ? window.URL.createObjectURL(data) : "");
};

export const checkIfImageUrl = (file: UploadFile): boolean => file.type !== undefined && !file.type.match("audio");

/**
 * Render a thumbnail based on HTML supported file type.
 *
 * @param {Thumbnails} thumbnail
 * @param {React.ReactElement} originNode
 * @param {UploadFile} file
 * @return {React.ReactNode}
 */
export const renderThumbnail = (
    thumbnail: Thumbnails, originNode: React.ReactElement, file: UploadFile,
): React.ReactNode => {
    const {type, name, originFileObj} : UploadFile = file;
    const fileType: string = type || "";
    if (HTML_SUPPORTED_VIDEO.includes(fileType) && originFileObj) {
        return (
            <div className="df-custom-list-item" >
                <div className="df-thumbnail-wrap">
                    <div className="df-thumbnail">
                        <VideoThumbnail
                            videoUrl={window.URL.createObjectURL(originFileObj)}
                            playable={thumbnail === Thumbnails.VIDEO_PLAYABLE}
                        />
                    </div>
                    <span className="ant-upload-list-item-name" title={name}>{name}</span>
                </div>
                {originNode}
            </div>
        );
    }
    return originNode;
};

/**
 * Render an antd icon in case HTML doesn't support a given file type.
 *
 * @param {UploadFile} file
 * @return {React.ReactNode}
 */
export const renderFallbackIcon = (file: UploadFile): React.ReactNode => {
    const {type}: UploadFile = file;
    const fileType: string = type || "";
    const media: Nullable<string> = fileType.split("/")[0];
    switch (media) {
        case "video":
            return <VideoCameraOutlined/>;
        case "audio":
            return <SoundOutlined/>;
        case "image":
            // returning spinner for heic images to simulate loading while they are converted
            if (type === "image/heic") {
                return <Spin><PictureOutlined/></Spin>;
            }
            return <PictureOutlined/>;
        default:
            return <FileOutlined/>;
    }
};

/**
 * Create error alert when bucket limit is reached.
 *
 * @param {ErrorMessageData} limitMessageData
 * @return {ArgsProps}
 */
export const createBucketLimitAlert = (limitMessageData: ErrorMessageData): ArgsProps => {
    const {
        validUploadsHolders, translate, translatePath,
    }: ErrorMessageData = limitMessageData;

    const limitReached: string = translate(`${translatePath}.limitReached.text`) as string;
    let uploadBuckets: string = "";
    validUploadsHolders.forEach((uploadHolder: UploadsHolder): void => {
        uploadBuckets = uploadBuckets.concat(`${uploadHolder.name}, `);
    });
    uploadBuckets = uploadBuckets.slice(0, uploadBuckets.lastIndexOf(","));
    const limitMessage: string = formatString(limitReached, uploadBuckets);

    return {
        content: limitMessage,
        duration: 0,
        type: "error",
        key: `${uploadBuckets}-limit`,
    };
};

/**
 * Create error alert, when maximum number of upload files is reached.
 *
 * @param {ErrorMessageData} limitMessageData
 * @return {ArgsProps}
 */
export const createUploadFilesLimitAlert = (limitMessageData: ErrorMessageData): ArgsProps => {
    const {translate, translatePath}: ErrorMessageData = limitMessageData;
    const limitReached: string = translate(`${translatePath}.uploadFilesLimitReached.text`) as string;

    return {
        content: limitReached,
        duration: 0,
        type: "error",
        key: "limit-reached",
    };
};

/**
 * Create corresponding error message for reopened file.
 *
 * @param {ErrorMessageData} errorMessageData
 * @return ArgsProps
 */
export const createReopenedFileAlert = (errorMessageData: ErrorMessageData): ArgsProps => {
    const {
        validUploadsHolders, translate, translatePath, upload,
    }: ErrorMessageData = errorMessageData;

    let errorMessage: string = translate(`${translatePath}.invalidFile.text`) as string;
    if (upload) {
        const reopenedHolder: Nullable<UploadsHolder> = validUploadsHolders.find(
            (holder: UploadsHolder) => holder.uploadsConfig.upload === upload,
        );
        if (reopenedHolder) {
            const error: string = translate(`${translatePath}.reopenedFileInvalid.text`) as string;
            errorMessage = formatString(error, reopenedHolder.name);
        }
    }

    return {
        content: errorMessage,
        duration: 0,
        type: "error",
        key: "reopened-error",
    };
};

/**
 * Determines index of first available UploadHolder (bucket), where file can be added.
 *
 * @param {AvailableHolderData} availableHolderData
 * @return {number}
 */
export const getAvailableHolderIndex = (availableHolderData: AvailableHolderData): number => {
    const {
        bucketFilesMap,
        uploadsHolders,
        validBatchUploadsConfig,
        bucketCount,
    }: AvailableHolderData = availableHolderData;
    return uploadsHolders.findIndex(
        (uploadsHolder: UploadsHolder): boolean => {
            let uploaded: number = uploadsHolder.uploadsConfig.upload && bucketCount[uploadsHolder.uploadsConfig.upload]
                ? bucketCount[uploadsHolder.uploadsConfig.upload]
                : 0;
            const bucketKey: string = determineBucketKey({
                useBucketConfig: !!uploadsHolders.length,
                validBatchUploadsConfig,
                uploadsHolder,
            });
            const uploadsBucket: UploadsBucket = bucketFilesMap[bucketKey];
            if (uploadsBucket) {
                uploaded += Object.values(uploadsBucket).length;
            }
            return !uploadsHolder.maxCount || uploaded < uploadsHolder.maxCount;
        },
    );
};

/**
 * Extracts file name from media URL
 *
 * @param mediaUrl
 * @return {string}
 */
export const getNameFromMediaUrl = (mediaUrl: Nullable<string>): string => {
    let fileName: string = "";
    if (mediaUrl) {
        const lastSlash: number = mediaUrl.lastIndexOf("/");
        const firstQuestionMark: number = mediaUrl.indexOf("?");
        if (lastSlash > -1 && firstQuestionMark > -1) {
            fileName = mediaUrl.substring(lastSlash + 1, firstQuestionMark);
        }
    }
    return fileName;
};

/**
 * Returns "Delete" icon with appropriate aria-label attribute.
 *
 * @param {IconData} iconData
 * @return {React.ReactNode}
 */
export const createDeleteIcon = (iconData: IconData): React.ReactNode => {
    const {fileName, translate, translatePath}: IconData = iconData;
    const deleteLabelMessage: string = translate(`${translatePath}.deleteFile.text`) as string;
    const deleteLabel: string = formatString(deleteLabelMessage, fileName);
    return (
        <DeleteOutlined aria-label={deleteLabel}/>
    );
};

/**
 * Returns "Preview" icon with appropriate aria-label attribute.
 *
 * @param {IconData} iconData
 * @return {React.ReactNode}
 */
export const createPreviewIcon = (iconData: IconData): React.ReactNode => {
    const {fileName, translate, translatePath}: IconData = iconData;
    const previewLabelMessage: string = translate(`${translatePath}.previewFile.text`) as string;
    const previewLabel: string = formatString(previewLabelMessage, fileName);
    return (
        <EyeOutlined aria-label={previewLabel}/>
    );
};
